import { Injectable, inject, signal } from '@angular/core';

import { HttpService, IHttpOptions } from '@partners/http';
import { isEmptyArray, isEmptyObject } from '@partners/utils';
import { Observable, lastValueFrom, throwError } from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import {
  TCatalogCollection,
  TCatalogEndPoint,
  TCatalogList,
  TCatalogOption,
  TCatalogResidenceList,
  TResidenceOption
} from '../model/catalog.interface';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  metadata$$ = signal<TCatalogCollection>({});
  finance$$ = signal<TCatalogCollection>({});
  residence$$ = signal<TCatalogResidenceList>([]);

  isMetadataInProgress = signal<boolean>(false);
  isFinanceInProgress = signal<boolean>(false);
  isResidenceInProgress = signal<boolean>(false);

  metadataPromise: Promise<TCatalogCollection>;
  financePromise: Promise<TCatalogCollection>;
  residencePromise: Promise<TCatalogResidenceList>;

  private _httpService = inject(HttpService);

  constructor() {}

  loadAll$(options: IHttpOptions = {}) {
    this.loadMetadata(options);
    this.loadFinance(options);
    this.loadResidence(options);
  }

  loadMetadata(options: IHttpOptions = {}): Promise<TCatalogCollection> {
    if (this.isMetaDataEmpty() && !this.metadataPromise) {
      this.isMetadataInProgress.set(true);
      const metadataCatalog = this.requestCatalog$<TCatalogCollection>('metadata', options).pipe(
        tap((data) => this.metadata$$.set(data)),
        finalize(() => this.isMetadataInProgress.set(false))
      );
      this.metadataPromise = lastValueFrom(metadataCatalog);
    }
    return this.metadataPromise;
  }

  loadFinance(options: IHttpOptions = {}): Promise<TCatalogCollection> {
    if (this.isFinanceEmpty() && !this.financePromise) {
      this.isFinanceInProgress.set(true);
      const financeCatalog = this.requestCatalog$<TCatalogCollection>('finance', options).pipe(
        tap((data) => this.finance$$.set(data)),
        finalize(() => this.isFinanceInProgress.set(false))
      );
      this.financePromise = lastValueFrom(financeCatalog);
    }
    return this.financePromise;
  }

  loadResidence(options: IHttpOptions = {}): Promise<TCatalogResidenceList> {
    if (this.isResidenceEmpty() && !this.residencePromise) {
      this.isResidenceInProgress.set(true);
      const residenceCatalog = this.requestCatalog$<TCatalogResidenceList>('residence', options).pipe(
        tap((data) => this.residence$$.set(data)),
        finalize(() => this.isResidenceInProgress.set(false))
      );

      this.residencePromise = lastValueFrom(residenceCatalog);
    }
    return this.residencePromise;
  }

  fromFinance<T = TCatalogOption>(key: string): T[] {
    const financeCatalog: TCatalogCollection = this.finance$$();
    return financeCatalog[key] as T[];
  }

  fromMetadata<T = TCatalogOption>(key: string): T[] {
    const metadataCatalog: TCatalogCollection = this.metadata$$();
    return metadataCatalog[key] as T[];
  }

  fromResidence(): TResidenceOption[] {
    const residenceCatalog: TCatalogResidenceList = this.residence$$();
    return residenceCatalog;
  }


  requestCatalog$<T>(endPoint: TCatalogEndPoint, options: IHttpOptions = {}): Observable<T> {
    // const context = new ContextBuilder().disableLoader().build();
    // options = { ...options, ...{ context } };

    return this._httpService.fetch<null, T>(`catalog/${endPoint}`, options).pipe(
      catchError((error) => {
        console.error(`\nFailed to load "${endPoint}" catalog\n`);
        return throwError(() => error);
      }),
      take(1),
      map(({ result }) => {
        return result;
      })
    );
  }

  private isResidenceEmpty(): boolean {
    return this.isEmptyList(this.residence$$());
  }

  private isFinanceEmpty(): boolean {
    return this.isEmptyCollection(this.finance$$());
  }

  private isMetaDataEmpty(): boolean {
    return this.isEmptyCollection(this.metadata$$());
  }

  private isEmptyCollection(collection: TCatalogCollection) {
    return isEmptyObject(collection);
  }

  private isEmptyList(list: TCatalogList) {
    return isEmptyArray(list);
  }
}
